import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Calendar,
  User,
  Mail,
  Phone,
  Star,
  ShieldCheck,
  ArrowRight,
  ChevronDown,
  CheckCircle2,
  X,
} from "lucide-react";
import apiClient from "../config/apiClient";

const STORAGE_KEY = "simplifyte_demo_lead_submitted_v2";
const EARLY_SHOWS = 1; // how many appearances use the short delay
const EARLY_DELAY_MS = 30000; // 30s, for the first EARLY_SHOWS appearances
const REPEAT_INTERVAL_MS = 3 * 60 * 1000; // every 3 min after that

export default function DemoLeadPopup() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 640
  );
  const hasSubmittedRef = useRef(
    typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY) === "true"
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (hasSubmittedRef.current) return;

    let timerId;
    let showCount = 0;

    const scheduleNext = () => {
      const delay = showCount < EARLY_SHOWS ? EARLY_DELAY_MS : REPEAT_INTERVAL_MS;

      timerId = setTimeout(() => {
        if (hasSubmittedRef.current) return;

        setVisible(true);
        setExpanded(true);
        showCount += 1;
        scheduleNext();
      }, delay);
    };

    scheduleNext();

    return () => clearTimeout(timerId);
  }, []);

  if (hasSubmittedRef.current) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
    setForm((prev) => ({ ...prev, phone: digitsOnly }));
  };

  const handleClose = () => {
    setVisible(false);
    setExpanded(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await apiClient.post("/sales-contact-form", {
        name: form.name,
        email: form.email,
        country_code: "+91",
        contact_number: form.phone,
        company_size: "2 - 10",
        message: "Requested a free demo via site popup.",
      });

      setSubmitted(true);
      hasSubmittedRef.current = true;
      localStorage.setItem(STORAGE_KEY, "true");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={
            isMobile
              ? { opacity: 0, y: "100%" }
              : { opacity: 0, y: 32, scale: 0.97 }
          }
          animate={
            isMobile
              ? { opacity: 1, y: 0 }
              : { opacity: 1, y: 0, scale: 1 }
          }
          exit={
            isMobile
              ? { opacity: 0, y: "100%" }
              : { opacity: 0, y: 24, scale: 0.97 }
          }
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className={
            isMobile
              ? "fixed inset-x-0 bottom-0 z-[9998] w-full max-h-[85vh] overflow-y-auto bg-white rounded-t-2xl shadow-[0_-10px_40px_-10px_rgba(9,58,91,0.35)] border-t border-slate-200/70"
              : "fixed bottom-[100px] right-6 z-[9998] w-[320px] sm:w-[368px] bg-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(9,58,91,0.35)] border border-slate-200/70 overflow-hidden"
          }
        >
          {/* Header */}
          <div
            className={`relative overflow-hidden px-5 py-4 cursor-pointer select-none ${
              isMobile ? "rounded-t-2xl" : ""
            }`}
            style={{
              background:
                "linear-gradient(135deg, #0c4468 0%, #093a5b 55%, #072b42 100%)",
            }}
            onClick={() => setExpanded((prev) => !prev)}
          >
            <div className="pointer-events-none absolute -top-8 -right-8 w-28 h-28 bg-white/10 rounded-full blur-2xl" />
            <div className="pointer-events-none absolute -bottom-10 -left-6 w-24 h-24 bg-[#1a8fe3]/20 rounded-full blur-2xl" />

            <div className="relative flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-white/15 border border-white/10 flex items-center justify-center">
                  <Calendar size={17} className="text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-white text-[13.5px] font-semibold leading-tight truncate">
                    Book a Free Demo
                  </p>
                  <p className="text-white/60 text-[11px] leading-tight">
                    10-min walkthrough, no commitment
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((prev) => !prev);
                  }}
                  className="text-white/70 hover:text-white p-1.5 rounded-full hover:bg-white/10 transition-colors"
                  aria-label={expanded ? "Minimize" : "Expand"}
                >
                  <motion.span
                    animate={{ rotate: expanded ? 0 : 180 }}
                    transition={{ duration: 0.2 }}
                    className="flex"
                  >
                    <ChevronDown size={16} />
                  </motion.span>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  className="text-white/70 hover:text-white p-1.5 rounded-full hover:bg-white/10 transition-colors"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>

          <AnimatePresence initial={false}>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="overflow-hidden"
              >
                <div className="px-5 pt-4 pb-5">
                  {submitted ? (
                    <div className="text-center py-5">
                      <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
                        <CheckCircle2 size={26} className="text-emerald-500" />
                      </div>
                      <h4 className="text-[14px] font-semibold text-slate-900 mb-1">
                        You're all set!
                      </h4>
                      <p className="text-[12.5px] text-slate-500 leading-relaxed">
                        Our team will reach out within 1-2 working days to
                        schedule your demo.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={12}
                            className="fill-amber-400 text-amber-400"
                          />
                        ))}
                        <span className="text-[11px] text-slate-500 font-medium ml-0.5">
                          Trusted by 500+ professional teams
                        </span>
                      </div>

                      <p className="text-[12.5px] text-slate-500 mb-4 leading-relaxed">
                        See how Simplifyte keeps your team's client work,
                        deadlines and documents on track.
                      </p>

                      <form onSubmit={handleSubmit} className="space-y-3">
                        <div className="relative">
                          <User
                            size={15}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                          />
                          <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Your full name"
                            required
                            className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2.5 text-[13px] text-slate-900 placeholder-slate-400 bg-slate-50/60 focus:bg-white focus:outline-none focus:border-[#093a5b] focus:ring-2 focus:ring-[#093a5b]/10 transition-colors"
                          />
                        </div>

                        <div className="relative">
                          <Mail
                            size={15}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                          />
                          <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="Work email"
                            required
                            className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2.5 text-[13px] text-slate-900 placeholder-slate-400 bg-slate-50/60 focus:bg-white focus:outline-none focus:border-[#093a5b] focus:ring-2 focus:ring-[#093a5b]/10 transition-colors"
                          />
                        </div>

                        <div className="flex items-center border border-slate-200 rounded-lg bg-slate-50/60 overflow-hidden focus-within:bg-white focus-within:border-[#093a5b] focus-within:ring-2 focus-within:ring-[#093a5b]/10 transition-colors">
                          <span className="flex items-center gap-1.5 pl-3 pr-2 py-2.5 text-[13px] text-slate-500 font-medium border-r border-slate-200">
                            <Phone size={14} className="text-slate-400" />
                            +91
                          </span>
                          <input
                            type="tel"
                            name="phone"
                            value={form.phone}
                            onChange={handlePhoneChange}
                            placeholder="Mobile number"
                            required
                            className="w-full px-3 py-2.5 text-[13px] text-slate-900 placeholder-slate-400 bg-transparent focus:outline-none"
                          />
                        </div>

                        {error && (
                          <p className="text-red-600 text-[11.5px] bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                          </p>
                        )}

                        <button
                          type="submit"
                          disabled={loading}
                          className="group w-full flex items-center justify-center gap-2 text-white text-[13.5px] font-semibold py-2.5 rounded-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-[#093a5b]/25 active:scale-[0.99]"
                          style={{ backgroundColor: "#093a5b" }}
                        >
                          {loading ? (
                            "Booking..."
                          ) : (
                            <>
                              Book a Free Demo
                              <ArrowRight
                                size={15}
                                className="transition-transform group-hover:translate-x-0.5"
                              />
                            </>
                          )}
                        </button>

                        <p className="flex items-center justify-center gap-1.5 text-[10.5px] text-slate-400 pt-0.5">
                          <ShieldCheck size={12} />
                          Your information is safe. No spam, ever.
                        </p>
                      </form>
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
